import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import PasswordInput from "../../components/PasswordInput";
import useCotacaoPwa from "./useCotacaoPwa";
import {
  Check, ArrowRight, LogIn, Sparkles, TrendingDown,
  FileSpreadsheet, Users, ShieldCheck, MessageCircle, Trophy,
} from "lucide-react";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const FEATURES = [
  { icon: Users, title: "Cadastro de fornecedores", desc: "Razão social, CNPJ, contato do vendedor, endereço e histórico completo por fornecedor." },
  { icon: FileSpreadsheet, title: "Mapa de cotação inteligente", desc: "Melhores propostas em verde no topo. Compare por item, forma de pagamento e frete." },
  { icon: MessageCircle, title: "Loja Digital do fornecedor", desc: "Envie o link por WhatsApp. O fornecedor preenche os preços item a item, direto do celular." },
  { icon: TrendingDown, title: "Economia por item", desc: "Veja o último preço comprado e quanto está economizando vs pior cotação — só o ADM enxerga." },
  { icon: Trophy, title: "Ranking de fornecedores e produtos", desc: "Dashboard mensal com valor comprado, top fornecedores e produtos mais comprados." },
  { icon: ShieldCheck, title: "Pedido por item, não por total", desc: "Aprove item a item — cada fornecedor entrega só o que ganhou. PDF pronto para envio." },
];

export default function CotacaoLandingPage() {
  useCotacaoPwa();
  const nav = useNavigate();
  const [signupOpen, setSignupOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", senha: "", empresa: "", telefone: "" });
  const [loading, setLoading] = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, email: (form.email || "").trim().toLowerCase() };
      const { data } = await axios.post(`${API}/cotacao/auth/register`, payload);
      localStorage.setItem("cotacao_token", data.token);
      localStorage.setItem("cotacao_user", JSON.stringify(data.user));
      toast.success("Conta criada! Você tem 14 dias grátis.");
      nav("/cotacoes/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao criar conta");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="cotacao-landing">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-compromisso.png" alt="CD Sistema de Cotações" className="w-10 h-10 rounded-xl shrink-0" />
            <div>
              <div className="font-bold leading-tight" style={{ fontFamily: "Work Sans" }}>Compromisso Digital</div>
              <div className="text-[11px] uppercase tracking-widest text-blue-700 font-semibold">Sistema de Cotações</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => nav("/cotacoes/login")} data-testid="btn-header-login">
              <LogIn className="w-4 h-4 mr-1" /> Entrar
            </Button>
            <Button size="sm" onClick={() => setSignupOpen(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="btn-header-signup">
              Testar grátis <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 pt-16 pb-12 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold mb-6">
          <Sparkles className="w-4 h-4" /> COTE ONLINE, ECONOMIZE OFFLINE
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
          Cotações de compra <br />
          <span className="bg-gradient-to-r from-blue-600 to-emerald-500 bg-clip-text text-transparent">no piloto automático</span>
        </h1>
        <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
          Selecione produtos, escolha fornecedores, envie o link no <b>WhatsApp</b> e receba as propostas
          item a item — com o melhor preço destacado em verde. <b>Aprove por item</b> e gere o pedido em PDF.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button size="lg" className="bg-blue-700 hover:bg-blue-800" onClick={() => setSignupOpen(true)} data-testid="btn-hero-signup">
            Começar teste grátis 14 dias <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button size="lg" variant="outline" onClick={() => nav("/cotacoes/login")} data-testid="btn-hero-login">
            Já tenho conta
          </Button>
        </div>
        <div className="mt-4 text-xs text-slate-500">
          Sem cartão de crédito para começar · Cadastro exclusivo do Sistema de Cotações
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <Card key={i} className="border-slate-200 hover:border-blue-300 transition">
              <CardContent className="p-5">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-3">
                  <f.icon className="w-5 h-5 text-blue-700" />
                </div>
                <div className="font-semibold mb-1">{f.title}</div>
                <div className="text-sm text-slate-600">{f.desc}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y">
        <div className="max-w-6xl mx-auto px-4 py-14">
          <h2 className="text-2xl font-bold text-center mb-10">Como funciona</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { n: 1, t: "Selecione produtos", d: "Escolha os itens que precisa cotar do seu catálogo." },
              { n: 2, t: "Selecione fornecedores", d: "Marque os fornecedores que vão receber a cotação." },
              { n: 3, t: "Envie por WhatsApp", d: "Link direto da Loja Digital exclusivo daquela cotação." },
              { n: 4, t: "Aprove por item e gere pedido", d: "Melhor preço em verde. Pedido em PDF por ganhador." },
            ].map((s) => (
              <div key={s.n} className="text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-blue-700 text-white font-bold flex items-center justify-center mb-3">{s.n}</div>
                <div className="font-semibold">{s.t}</div>
                <div className="text-sm text-slate-600 mt-1">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="text-3xl font-bold">Pronto para economizar em cada compra?</h2>
        <p className="mt-3 text-slate-600">Teste 14 dias grátis. Sem cartão. Cancele quando quiser.</p>
        <Button size="lg" className="mt-6 bg-blue-700 hover:bg-blue-800" onClick={() => setSignupOpen(true)} data-testid="btn-cta-signup">
          Criar conta grátis <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </section>

      <footer className="border-t bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 py-6 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Compromisso Digital · Sistema de Cotações
        </div>
      </footer>

      {/* Signup dialog */}
      <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-cotacao-signup">
          <DialogHeader>
            <DialogTitle>Criar conta — Sistema de Cotações</DialogTitle>
            <DialogDescription>14 dias grátis, sem cartão de crédito.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSignup} className="space-y-3">
            <div>
              <Label htmlFor="c-nome">Nome completo</Label>
              <Input id="c-nome" required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="signup-nome" />
            </div>
            <div>
              <Label htmlFor="c-empresa">Empresa (opcional)</Label>
              <Input id="c-empresa" value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} data-testid="signup-empresa" />
            </div>
            <div>
              <Label htmlFor="c-tel">WhatsApp (opcional)</Label>
              <Input id="c-tel" value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="signup-telefone" />
            </div>
            <div>
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email" />
            </div>
            <div>
              <Label htmlFor="c-senha">Senha</Label>
              <PasswordInput id="c-senha" required value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} data-testid="signup-senha" />
            </div>
            <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800" disabled={loading} data-testid="signup-submit">
              {loading ? "Criando..." : "Começar teste grátis"} <Check className="w-4 h-4 ml-1" />
            </Button>
            <div className="text-center text-xs text-slate-500">
              Já tem conta? <button type="button" className="text-blue-700 hover:underline" onClick={() => { setSignupOpen(false); nav("/cotacoes/login"); }}>Entrar</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
